import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { v1 as uuidv1 } from 'uuid'
import { tabMetrics } from '@/ContextTab/services/tabMetrics'
import { legacyBridge } from '@/services/legacyBridge'
import { TokenStorage } from '@/ContextTab/stores/auth/tokenStorage'
import { apiClientV1, apiClientV2, type ApiError } from '@/ContextTab/services/apiClient'
import type { StoredAuthInfo } from '@/ContextTab/stores/auth/tokenStorage'
import type { LostPasswordPostIn, ResetPasswordPostIn } from '@/api/generated/v1'
import type { BodyAuthLoginAuthLoginPost } from '@/api/generated/v2'


interface TokenData {
  fresh: boolean
  iat: number
  jti: string
  type: 'access' | 'refresh'
  sub: number
  nbf: number
  exp: number
  permissions: string[]
}

export enum AuthStatus {
  initial = 'initial',
  notAuthorized = 'notAuthorized',
  authorized = 'authorized',
}

function generateSessionId() {
  return uuidv1().replace(/-/g, '')
}

export const useAuthStore = defineStore('auth', () => {

  const login = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const accessToken = ref<string | null>(null)
  const sessionId = ref<string>(generateSessionId())

  const status = ref<AuthStatus>(AuthStatus.initial)
  const error = ref<{ errors: ApiError[] } | null>(null)

  const tokenStorage = new TokenStorage({
    onTokenChanged: (info: StoredAuthInfo | null) => {
      if (info) {
        login.value = info.login
        refreshToken.value = info.refreshToken
        accessToken.value = info.accessToken
      } else {
        logout()
      }
    },
    onSessionChanged: (newSessionId: string | null) => {
      if (newSessionId) {
        sessionId.value = newSessionId
      } else {
        tabMetrics.sendException('SessionId loaded from storage is null')
        regenerateSessionId()
      }
    },
  })

  const tokenData = computed<TokenData | null>(() => {
    const data: TokenData | null = accessToken.value
      ? jwtDecode(accessToken.value)
      : null

    if (data) {
      data.sub = Number(data.sub) // https://jira.interfax.ru/browse/PUB-4023
    }

    return data
  })

  const userId = computed(() => tokenData.value?.sub ?? null)

  const isAuthorized = computed(() => status.value === AuthStatus.authorized)

  function regenerateSessionId() {
    sessionId.value = generateSessionId()
    tokenStorage.saveSessionId(sessionId.value)
  }

  async function loadAndValidateTokens(): Promise<boolean> {
    const storedInfo = tokenStorage.loadAuthInfo()

    if (!storedInfo) {
      status.value = AuthStatus.notAuthorized
      return false
    }

    sessionId.value = tokenStorage.loadSessionId() ?? sessionId.value

    if (!(await refresh(storedInfo.refreshToken, storedInfo.login))) {
      // token is outdated
      return false
    }

    status.value = AuthStatus.authorized

    return true
  }

  // called on tab open and by timer from GW worker
  async function refresh(
    explicitRefreshToken: string | null = null,
    explicitLogin: string | null = null,
  ): Promise<StoredAuthInfo | null> {

    console.log('🎯 token refresh request')

    explicitRefreshToken = explicitRefreshToken ?? refreshToken.value
    explicitLogin = explicitLogin ?? login.value

    if (!explicitRefreshToken || !explicitLogin) {
      console.log(`🔥 explicit things are empty at refresh: ${
        !explicitRefreshToken ? 'refreshToken' : ''
      } ${!explicitLogin ? 'login' : ''}`)

      status.value = AuthStatus.notAuthorized
      return null
    }

    if (tokenStorage.isFreshToken(explicitRefreshToken, accessToken.value)) {
      console.log('🍏 token is fresh')
      return {
        login: explicitLogin,
        refreshToken: explicitRefreshToken,
        accessToken: accessToken.value!,
      }
    }

    console.log('🔑 api.refresh')
    const response = await apiClientV2.AuthService.authRefreshAuthRefreshPost({
      auth: explicitRefreshToken,
    })

    if (response.error) {
      status.value = AuthStatus.notAuthorized
      return null
    }

    saveToken(response.data.access_token, response.data.refresh_token, explicitLogin)

    return {
      login: explicitLogin,
      refreshToken: response.data.refresh_token,
      accessToken: response.data.access_token,
    }
  }

  function logout() {
    tokenStorage.clearAuthInfo()
    sessionStorage.clear()
    accessToken.value = null
    refreshToken.value = null
    login.value = null
    status.value = AuthStatus.notAuthorized
    regenerateSessionId()
    // page reload expected to clear news stores
  }

  // call only when token is just updated
  function saveToken(access: string, refresh: string, username: string) {
    if (!access || !refresh) {
      return
    }
    accessToken.value = access
    refreshToken.value = refresh
    login.value = username

    tokenStorage.saveAuthInfo(
      {
        login: username,
        refreshToken: refresh,
        accessToken: access,
      },
      true,
    )

    legacyBridge.updateToken(access, refresh)
  }

  async function authorize({ username, password }: BodyAuthLoginAuthLoginPost) {
    regenerateSessionId()
    error.value = null
    const response = await apiClientV2.AuthService.authLoginAuthLoginPost({ body: { username, password } })

    if (response.error) {
      status.value = AuthStatus.notAuthorized
      error.value = response.error
      return
    }

    saveToken(response.data.access_token, response.data.refresh_token, username)
    status.value = AuthStatus.authorized

    return response
  }

  async function requestPasswordReset({ username }: LostPasswordPostIn) {
    error.value = null
    const response = await apiClientV1.AuthService.postAuthLostPassword({ body: { username } })
    if (response.error) {
      error.value = response.error
    }
  }

  async function resetPassword(token: ResetPasswordPostIn) {
    error.value = null
    const response = await apiClientV1.AuthService.postAuthResetPassword({ body: token })
    if (response.error) {
      error.value = response.error
    }
  }

  function setError(errorMessage: string) {
    error.value = error.value ?? { errors: [] }
    error.value?.errors?.push({
      detail: errorMessage,
      code: uuidv1(),
    })
  }

  // watch(() => status.value, (status) => {
  //   if (status === AuthStatus.authorized) {
  //     tokenStorage.startTokenUpdate()
  //   } else {
  //     tokenStorage.stopTokenUpdate()
  //   }
  // })

  return {
    sessionId,
    refreshToken,
    accessToken,
    status,
    error,
    login,
    userId,
    isAuthorized,
    tokenData,
    regenerateSessionId,
    authorize,
    refresh,
    setError,
    loadAndValidateTokens,
    logout,
    requestPasswordReset,
    resetPassword,
  }
})
